import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isCorpTenant, useIsTravelApp, useUserContext, getConfigTenant } from "@capone/common";
import queryString from "query-string";
import corporateConfig from "../../utils/capone-corporate/config";

// note: the implementation is taken from https://drive.google.com/file/d/1xbsZZ0rt48nJNb7BlxpwcBKX4wUB6_Qw/view?ts=612f9085
const injectCempDigital = () => {
  (window as any).activateCempDigital = (function (wdw: any) {
    let CEMP_DIGITAL_PROPERTY_URL =
      "https://resources.digital-cloud.medallia.com/wdcus/160752/onsite/embed.js";
    let CEMP_DIGITAL_FORMS_TO_PRELOAD: (string | number)[] = [];

    if (isCorpTenant(corporateConfig.TENANT)) {
      CEMP_DIGITAL_PROPERTY_URL =
        corporateConfig.cempDigitalPropertyUrl || CEMP_DIGITAL_PROPERTY_URL;
      CEMP_DIGITAL_FORMS_TO_PRELOAD =
        corporateConfig.cempDigitalFormsToPreload ||
        CEMP_DIGITAL_FORMS_TO_PRELOAD;
    }

    const CEMP_DIGITAL_STATIC_PARAMS = {
      surveyProgram: "CEMP Digital",
      appType: "Web",
      lob: "OTHER",
    };
    const CEMP_DIGITAL_DYNAMIC_PARAMS: string[] = [];

    return function () {
      return new Promise((resolve) => {
        wdw.cempDataLayer = wdw.cempDataLayer || {};
        Object.keys(CEMP_DIGITAL_STATIC_PARAMS).forEach((param) => {
          wdw.cempDataLayer[param] = CEMP_DIGITAL_STATIC_PARAMS[param];
        });
        CEMP_DIGITAL_DYNAMIC_PARAMS.forEach((param) => {
          wdw.cempDataLayer[param] =
            wdw.cempDataLayer[param] === undefined
              ? null
              : wdw.cempDataLayer[param];
        });
        if (wdw.cempDigital == null) {
          const cempDigitalLoader = document.createElement("script");
          cempDigitalLoader.async = true;
          document.body.appendChild(cempDigitalLoader);
          cempDigitalLoader.src = CEMP_DIGITAL_PROPERTY_URL;
        }
        const initializeCempDigital = function () {
          wdw.cempDigital = wdw.KAMPYLE_ONSITE_SDK;
          CEMP_DIGITAL_FORMS_TO_PRELOAD.forEach((surveyId) => {
            wdw.cempDigital.loadForm(surveyId);
          });
          const cempDigitalInitialized = document.createEvent("Event");
          cempDigitalInitialized.initEvent(
            "cempDigitalInitialized",
            true,
            true
          );
          window.dispatchEvent(cempDigitalInitialized);
          resolve(true);
        };

        wdw.addEventListener("neb_OnsiteLoaded", initializeCempDigital, false);
      });
    };
  })(window);

  const cempDigitalInjected = document.createEvent("Event");
  cempDigitalInjected.initEvent("cempDigitalInjected", true, true);
  window.dispatchEvent(cempDigitalInjected);
};

export const activateCempDigital = () => {
  const wdw = window as any;
  if (cempDigitalIsEnabled() == true) {
    initializeCempDataLayer(wdw);
    initializeCempDigital(wdw);
  }
};

export const cempDigitalIsEnabled = () => true; // Add feature flag logic here

export const initializeCempDataLayer = (wdw: any) => {
  const params = queryString.parse(window.location.search);
  const isTravelApp =
    typeof params.isTravelApp === "string"
      ? params.isTravelApp === "true"
      : params.isTravelApp?.[0] === "true";

  wdw.cempDataLayer = wdw.cempDataLayer || {};
  wdw.cempDataLayer.country = "USA"; // Add more CEMP Data Layer assignment as appropriate
  wdw.cempDataLayer.responseUrl = window.location.href;
  // corporate travel cemp survey pulls from responseURL instead
  wdw.cempDataLayer.responseURL = window.location.href;
  wdw.cempDataLayer.appType = isTravelApp ? "App" : "Web";
};

export const initializeCempDigital = (wdw: any) => {
  function startCempDigital() {
    if (
      wdw.activateCempDigital != null &&
      typeof wdw.activateCempDigital === "function"
    ) {
      wdw.activateCempDigital();
      return true;
    }
    return false;
  }
  if (startCempDigital() == false) {
    window.addEventListener("cempDigitalInjected", startCempDigital, false);
  }
};

const onUrlChange = (location: string, isTravelApp?: boolean) => {
  const wdw = window as any;
  if (typeof wdw.cempDigital?.updatePageView === "function") {
    wdw.cempDataLayer.responseUrl = location;
    // corporate travel cemp survey pulls from responseURL instead
    wdw.cempDataLayer.responseURL = location;
    wdw.cempDigital.updatePageView();
    wdw.cempDataLayer.appType = isTravelApp ? "App" : "Web";
  }
};

export const useMedalliaFeedback = () => {
  const { isTravelApp } = useIsTravelApp();
  const { sessionInfo } = useUserContext(getConfigTenant());

  useEffect(() => {
    // Medallia's vendored script assumes it's running as the top-level page and throws
    // (`boundHandleLoadCustomBtnEvent` on undefined) inside a third-party iframe, e.g. the
    // Brex embed -- taking the surrounding render down with it. Skip loading it there.
    if (window.self !== window.top) return;

    const loadMedalliaFeedback = async () => {
      await injectCempDigital();
      activateCempDigital();
    };
    loadMedalliaFeedback();
  }, []);

  useEffect(() => {
    if (sessionInfo?.userId) {
      const wdw = window as any;
      wdw.cempDataLayer = wdw.cempDataLayer || {};
      wdw.cempDataLayer.ccid = sessionInfo.userId;
    }
  }, [sessionInfo?.userId]);

  const location = useLocation();

  useEffect(() => {
    onUrlChange(location.pathname, isTravelApp);
  }, [location, isTravelApp]);
};

export const useMedalliaBookingData = (reservationId?: string) => {
  useEffect(() => {
    if (reservationId) {
      const wdw = window as any;
      wdw.cempDataLayer = wdw.cempDataLayer || {};
      wdw.cempDataLayer.sessionId = reservationId;
      if (typeof wdw.cempDigital?.updatePageView === "function") {
        wdw.cempDigital.updatePageView();
      }
    }
  }, [reservationId]);
};
